import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { testApi, classApi, studentApi } from '@/lib/api';

import { Test, Class, Student } from '@/types';
import TestCreationProgress from '@/components/tests/TestCreationProgress';
import {
  ArrowLeft,
  Users,
  User,
  Calendar,
  Clock,
  BookOpen,
  Play,
  Save,
  Search
} from 'lucide-react';

const TestAssignPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // State from navigation
  const testTitle = location.state?.testTitle || 'Test';
  const scheduleData = location.state?.scheduleData || {};
  const mode = location.state?.mode || 'create';

  // Component state
  const [test, setTest] = useState<Test | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [testId]);

  const loadData = async () => {
    if (!testId) {
      console.error('TestAssignPage: No testId provided');
      navigate('/tests');
      return;
    }

    try {
      setDataLoading(true);
      console.log('TestAssignPage: Loading data for testId:', testId);

      const [testData, classesData, studentsData] = await Promise.all([
        testApi.getActualTestById(testId),
        classApi.getAll(),
        studentApi.getAll()
      ]);

      console.log('TestAssignPage: Loaded test data:', testData);
      console.log('TestAssignPage: Loaded classes data:', classesData);
      console.log('TestAssignPage: Loaded students data:', studentsData);

      setTest(testData);
      setClasses(Array.isArray(classesData) ? classesData : (classesData as any)?.content || []);
      setStudents(Array.isArray(studentsData) ? studentsData : (studentsData as any)?.content || []);
    } catch (error) {
      console.error('TestAssignPage: Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load assignment data. Please try again.',
        variant: 'destructive',
      });

      // Don't redirect on error - let user try again or use back button
    } finally {
      setDataLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/tests/schedule/${testId}`, {
      state: {
        testTitle: testTitle,
        mode: mode
      }
    });
  };

  const handleClassSelection = (classId: string, checked: boolean) => {
    if (checked) {
      setSelectedClassIds(prev => [...prev, classId]);
    } else {
      setSelectedClassIds(prev => prev.filter(id => id !== classId));
    }
  };

  const handleStudentSelection = (studentId: string, checked: boolean) => {
    if (checked) {
      setSelectedStudentIds(prev => [...prev, studentId]);
    } else {
      setSelectedStudentIds(prev => prev.filter(id => id !== studentId));
    }
  };

  const handleAssignAndPublish = async () => {
    if (!testId) return;

    if (selectedClassIds.length === 0 && selectedStudentIds.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one class or student to assign the test.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      // Prepare assignment data (test should already be scheduled from previous step)
      const assignmentData = {
        classIds: selectedClassIds.length > 0 ? selectedClassIds : undefined,
        studentIds: selectedStudentIds.length > 0 ? selectedStudentIds : undefined,
        allowLateSubmission: false,
        shuffleQuestions: false,
        shuffleOptions: false,
        maxAttempts: 1,
        instructions: "Complete this test within the given time limit.",
        adminNotes: "Test assigned via admin portal"
      };

      // Assign and publish the test (test should already be scheduled)
      await testApi.assignAndPublish(testId, assignmentData);

      toast({
        title: 'Success',
        description: 'Test has been assigned and published successfully!',
      });

      navigate('/tests');
    } catch (error) {
      console.error('Failed to assign and publish test:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign and publish test. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!testId) return;

    try {
      setLoading(true);

      // Just finalize the test as draft
      await testApi.finalizeTest(testId);

      toast({
        title: 'Success',
        description: 'Test saved as draft successfully!',
      });

      navigate('/tests');
    } catch (error) {
      console.error('Failed to save test as draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to save test. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student =>
    (student.firstName + ' ' + student.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTotalSelectedStudents = () => {
    const classStudentCount = selectedClassIds.reduce((total, classId) => {
      const classData = classes.find(c => c.id === classId);
      return total + (classData?.studentCount ?? classData?.studentIds.length ?? 0);
    }, 0);
    return classStudentCount + selectedStudentIds.length;
  };

  if (dataLoading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading assignment options...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Progress Indicator - Only show during creation flow */}
      {mode === 'create' && (
        <TestCreationProgress currentStep="assign" testTitle={testTitle} />
      )}

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Assign Test</h1>
            <p className="text-muted-foreground mt-1">
              Assign "{testTitle}" to classes and students
            </p>
          </div>
          <Badge variant="outline" className="text-sm">
            Step 4 of 4
          </Badge>
        </div>

        {/* Test Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Test Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {scheduleData.scheduleType === 'scheduled' ? 'Scheduled Test' :
                   scheduleData.scheduleType === 'open' ? 'Open Test' : 'Draft Test'}
                </span>
              </div>
              {test && (
                <>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{test.duration} minutes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{test.totalQuestions} questions</span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Assignment Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Assignment Targets</CardTitle>
            <p className="text-sm text-muted-foreground">
              Select classes and/or individual students to assign this test to.
            </p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="classes" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="classes" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Classes ({selectedClassIds.length})
                </TabsTrigger>
                <TabsTrigger value="students" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Individual Students ({selectedStudentIds.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="classes" className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Select entire classes to assign the test to all students in those classes.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                  {classes.map((classItem) => (
                    <div
                      key={classItem.id}
                      className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`class-${classItem.id}`}
                        checked={selectedClassIds.includes(classItem.id)}
                        onCheckedChange={(checked) =>
                          handleClassSelection(classItem.id, checked === true)
                        }
                      />
                      <Label
                        htmlFor={`class-${classItem.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div>
                          <div className="font-medium">{classItem.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {classItem.studentCount ?? classItem.studentIds.length ?? 0} students
                          </div>
                        </div>
                      </Label>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="students" className="space-y-4">
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    Select individual students to assign the test to.
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search students by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                  {filteredStudents.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`student-${student.id}`}
                        checked={selectedStudentIds.includes(student.id)}
                        onCheckedChange={(checked) =>
                          handleStudentSelection(student.id, checked === true)
                        }
                      />
                      <Label
                        htmlFor={`student-${student.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div>
                          <div className="font-medium">{student.firstName} {student.lastName}</div>
                          <div className="text-sm text-muted-foreground">
                            {student.email}
                          </div>
                        </div>
                      </Label>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            {/* Selection Summary */}
            {(selectedClassIds.length > 0 || selectedStudentIds.length > 0) && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm font-medium text-blue-900">
                  Assignment Summary
                </div>
                <div className="text-sm text-blue-700 mt-1">
                  Total students to be assigned: {getTotalSelectedStudents()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Schedule
          </Button>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSaveAsDraft}
              disabled={loading}
            >
              <Save className="mr-2 h-4 w-4" />
              Save as Draft
            </Button>

            <Button
              onClick={handleAssignAndPublish}
              disabled={loading || (selectedClassIds.length === 0 && selectedStudentIds.length === 0)}
            >
              <Play className="mr-2 h-4 w-4" />
              Assign & Publish Test
            </Button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TestAssignPage;
